import { useState } from "react";
import { ApiError, saveNote } from "../api";
import type { NoteKind, NoteSummary } from "../api";
import { Time } from "../Time";
import { ROLE_LABEL } from "../pages/CustomerLayout";

export const NOTE_META: Record<NoteKind, { title: string; desc: string }> = {
  kpi: { title: "KPI notes", desc: "Targets and metric definitions agreed with the customer" },
  updates: { title: "Updates", desc: "General notes and the latest status" },
};

/**
 * One note: its current text, and an editor that saves a new version.
 *
 * The editor remembers which version it opened on and sends it with the
 * save. If somebody else saved in between, the server refuses with a 409
 * and keeps nothing; the draft stays here, the newer text is shown above
 * it, and saving again is a deliberate choice to replace what they wrote.
 */
export function NoteCard({
  customer,
  note,
  canEdit,
  onChanged,
  onHistory,
}: {
  customer: string;
  note: NoteSummary;
  canEdit: boolean;
  /** Refetch the notes, after a save or a conflict. */
  onChanged: () => void;
  onHistory: () => void;
}) {
  const meta = NOTE_META[note.kind];
  const current = note.current;
  const [draft, setDraft] = useState<string | null>(null);
  const [base, setBase] = useState(0);
  const [conflict, setConflict] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editing = draft !== null && canEdit;
  // After a conflict the base is whatever is current now: the person has
  // been shown it, so saving again replaces it knowingly.
  const baseVersion = conflict ? (current?.version ?? 0) : base;
  const unchanged = (draft ?? "").trim() === (current?.body ?? "").trim();

  const start = () => {
    setDraft(current?.body ?? "");
    setBase(current?.version ?? 0);
    setConflict(false);
    setError(null);
  };
  const cancel = () => {
    setDraft(null);
    setConflict(false);
    setError(null);
  };

  const save = async () => {
    if (draft === null) return;
    setBusy(true);
    setError(null);
    try {
      await saveNote(customer, note.kind, draft, baseVersion);
      setDraft(null);
      setConflict(false);
      onChanged();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setConflict(true);
        onChanged();
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card note">
      <div className="note-head">
        <div className="note-title">
          <h3>{meta.title}</h3>
          <div className="meta">{meta.desc}</div>
        </div>
        <div className="note-actions">
          {note.version_count > 0 && (
            <button className="tiny" onClick={onHistory}>
              History · {note.version_count}
            </button>
          )}
          {canEdit && !editing && (
            <button className="tiny" onClick={start}>
              {current ? "Edit" : "Add"}
            </button>
          )}
        </div>
      </div>

      {editing && conflict && (
        <div className="error note-conflict">
          Somebody saved a newer version while you were editing. It is shown below; your draft
          is kept. Save again to replace it, or cancel to keep theirs.
          {current && <div className="note-text">{current.body}</div>}
        </div>
      )}

      {editing ? (
        <div className="note-editor">
          <textarea
            rows={8}
            value={draft ?? ""}
            onChange={(e) => setDraft(e.target.value)}
            disabled={busy}
            autoFocus
          />
          <div className="note-editor-bar">
            <button className="primary" onClick={save} disabled={busy || unchanged}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button onClick={cancel} disabled={busy}>
              Cancel
            </button>
            <span className="dim">Saving adds a new version. Earlier versions stay in history.</span>
          </div>
          {error && <div className="error">{error}</div>}
        </div>
      ) : current ? (
        <div className="note-text">{current.body}</div>
      ) : (
        <div className="dim">Nothing written yet.</div>
      )}

      {current && (
        <div className="note-foot">
          <span>
            Last edited by {current.author_name ?? current.author_email} (
            {ROLE_LABEL[current.author_role]})
          </span>
          <span>·</span>
          <Time value={current.created_at} mode="relative" />
        </div>
      )}
    </section>
  );
}
